import { cn } from '../lib/cn';
import { humanMessage } from '../lib/errors';
import { Button } from './Button';
import { Icon, type IconName } from './Icon';
import { Panel } from './Panel';

export function LoadingState({ label = 'Готуємо арену…' }: { label?: string }): React.JSX.Element {
  return (
    <div className="grid min-h-[50vh] place-items-center px-4" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-4">
        <span className="lk-anim-pulse grid h-16 w-16 place-items-center rounded-full bg-[var(--lk-gold)]">
          <Icon name="hourglass" size={34} />
        </span>
        <p className="text-lg font-black text-white/80">{label}</p>
      </div>
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  title?: string;
  className?: string;
}

/** Помилку показуємо завжди й людською мовою. Мовчазних «нічого не сталося» немає. */
export function ErrorState({
  error,
  onRetry,
  title = 'Не вийшло завантажити',
  className,
}: ErrorStateProps): React.JSX.Element {
  return (
    <Panel className={cn('mx-auto my-6 max-w-md p-5 text-center', className)} role="alert">
      <Icon name="wrong" size={56} className="mx-auto" />
      <h2 className="mt-2 text-xl font-black">{title}</h2>
      <p className="mt-2 text-sm font-semibold text-white/70">{humanMessage(error)}</p>
      {onRetry ? (
        <Button tone="gold" className="mt-4" onClick={onRetry}>
          Спробувати ще раз
        </Button>
      ) : null}
    </Panel>
  );
}

export function EmptyState({
  icon = 'empty',
  title,
  hint,
  className,
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={cn('px-4 py-10 text-center', className)}>
      <Icon name={icon} size={56} className="mx-auto" />
      <p className="mt-2 text-lg font-black">{title}</p>
      {hint ? <p className="mt-1 text-sm font-semibold text-white/60">{hint}</p> : null}
    </div>
  );
}

/**
 * Дружній екран «немає інтернету». Ніякого білого аркуша з помилкою:
 * дитина має розуміти, що все гаразд і прогрес у безпеці.
 */
export function OfflineScreen({
  pending,
  onRetry,
}: {
  pending: number;
  onRetry?: () => void;
}): React.JSX.Element {
  return (
    <div className="lk-shell lk-safe grid min-h-[70vh] place-items-center">
      <Panel className="w-full p-6 text-center">
        <Icon name="offline" size={88} className="lk-anim-float mx-auto" label="Немає інтернету" />
        <h1 className="mt-3 text-2xl font-black">Інтернет загубився</h1>
        <p className="mt-2 text-sm font-semibold text-white/70">
          Це не страшно. Усе, що ти вже відповів, збережено на пристрої.
          {pending > 0 ? ` Зараз у черзі ${pending}.` : ''} Щойно звʼязок повернеться — надішлемо все
          автоматично.
        </p>
        {onRetry ? (
          <Button tone="cyan" className="mt-5" onClick={onRetry}>
            Перевірити звʼязок
          </Button>
        ) : null}
      </Panel>
    </div>
  );
}
